import React from 'react';
import { RotateCw, Trash2, X } from 'lucide-react';
import { signerColor } from './sign-overlays';

/**
 * SignSignerPanel — the right pane of step 2 when a SIGNER is selected.
 *
 * This is where step 3 went. Its one surviving card asked two questions per
 * party — which column holds their address, and what the address is — and
 * asked them on a screen of their own, two clicks away from the signer being
 * configured. Here they are asked about the signer you already have in hand.
 *
 * The panel obeys the same rule the field panel does: click something on the
 * left or on the page, edit it on the right. Selecting a signer row was always
 * a real selection — it decides which party owns the next chip you drop — it
 * just had nothing to show for itself.
 *
 * ROLE NAME is editable here as well as inline via the row's pencil. Two paths
 * on purpose: the pencil is the quick one, this is the one you find without
 * looking for it. The label is load-bearing (templates merge parties BY LABEL),
 * so it is worth being findable.
 *
 * What is NOT a rename path any more is clicking the row itself. That used to
 * start an inline edit on the already-active row, which stopped working the day
 * this panel existed: clicking a signer has to mean "show me this signer", or
 * there is no way to reach the address field at all.
 */
export default function SignSignerPanel({
  signer,
  signerIndex,
  signerCount,
  emailColumns,
  documentCount,
  fieldCount,
  documentsSigned,
  hasSignature,
  resolving,
  onPatch,
  onResolveFromColumn,
  onCreateColumn,
  onRemove,
  onClose,
}) {
  const style = signerColor(signerIndex);
  const label = signer.roleLabel || `Signer ${signerIndex + 1}`;
  // A column is chosen and there is still no address: the read came back with
  // nothing. `!resolving` so the in-flight moment reads as "reading" rather
  // than flashing "empty" before the answer arrives.
  const columnEmpty = Boolean(signer.emailColumnId) && !String(signer.email || '').trim() && !resolving;

  return (
    <aside className='flex flex-col flex-shrink-0 w-72 bg-white border-l border-gray-200 overflow-y-auto'>
      <div className='flex items-center justify-between flex-shrink-0 px-4 py-3 border-b border-gray-100'>
        <div className='flex items-center gap-2 min-w-0'>
          <span className='w-2.5 h-2.5 rounded-full flex-shrink-0' style={{ background: style.color }} />
          <p className='text-sm font-semibold truncate text-gray-900'>{label}</p>
        </div>
        <button type='button' onClick={onClose} aria-label='Close' className='p-1 rounded hover:bg-gray-100'>
          <X size={15} className='text-gray-400' />
        </button>
      </div>

      <div className='flex-1 px-4 py-4 space-y-5'>
        <PanelField label='Signer title'>
          <input
            type='text'
            value={signer.roleLabel || ''}
            maxLength={80}
            placeholder={`Signer ${signerIndex + 1}`}
            onChange={(e) => onPatch({ roleLabel: e.target.value })}
            className='w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none'
          />
        </PanelField>

        {/*
          The column comes first, and it is the recommended path: on an envelope
          it resolves against the real item the moment it is picked, so one
          choice fills the address in. Typing stays available for a party the
          board does not hold — an outside counterparty, a witness.
        */}
        <PanelField label='Email column'>
          <select
            value={signer.emailColumnId || ''}
            disabled={resolving}
            onChange={(e) => {
              if (e.target.value === '__create__') onCreateColumn();
              else if (e.target.value) onResolveFromColumn(e.target.value);
              else onPatch({ emailColumnId: null });
            }}
            className='w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none disabled:opacity-60'
          >
            <option value=''>Type an address instead</option>
            {(emailColumns || []).map((col) => (
              <option key={col.id} value={col.id}>
                {col.title}
              </option>
            ))}
            <option value='__create__'>+ Create new email column…</option>
          </select>
          {resolving && (
            <p className='text-xs text-gray-500 mt-1 flex items-center gap-1'>
              <RotateCw size={10} className='animate-spin' />
              Reading this item…
            </p>
          )}
          {/*
            Derived, not remembered: a column chosen with no address beside it
            IS the empty-column state, so it keeps saying so for as long as it
            is true rather than for four seconds. Filling the address in by
            hand clears it, which is exactly the escape it points at.
          */}
          {columnEmpty && (
            <p className='text-xs text-amber-700 mt-1'>This column is empty on this item.</p>
          )}
        </PanelField>

        {/*
          The asterisk is HERE, not on the column above it. The column is
          satisfied by any choice — including "Type an address instead", which
          is the option that makes this field mandatory — whereas the send gate
          checks `signer.email` and nothing else, whether it was typed or read
          from the item. Marking the column would point at the control that is
          never the one blocking you.
        */}
        <PanelField label='Address' required>
          <input
            type='email'
            value={signer.email || ''}
            maxLength={320}
            placeholder='name@company.com'
            onChange={(e) => onPatch({ email: e.target.value })}
            className='w-full px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none'
          />
          <p className='text-xs text-gray-500 mt-1'>
            {columnEmpty
              ? 'Nothing to read from that column — type the address here.'
              : !!signer.emailColumnId && 'Read from the item. Edit to override it for this request.'}
          </p>
        </PanelField>

        {/*
          Coverage, not a warning.

          What this party signs is the question the panel is next to — the
          document rail is one pane away and the boxes are on screen. Whether
          they are MISSING a signature is said once, by name, in the footer hint
          beside the primary button, which is also what blocks the send. Saying
          it here as well is the noise the palette rows were already trimmed of.
        */}
        <div className='pt-3 border-t border-gray-100'>
          <p className='text-xs font-medium text-gray-500 mb-1.5'>Signs on</p>
          <p className='text-sm text-gray-900'>
            {documentsSigned} of {documentCount} document{documentCount === 1 ? '' : 's'}
            <span className='text-gray-400'> · </span>
            {fieldCount} field{fieldCount === 1 ? '' : 's'}
          </p>
          {!hasSignature && (
            <p className='text-xs mt-1' style={{ color: '#d83a52' }}>
              No signature field yet — drop one from the palette.
            </p>
          )}
        </div>

        {/*
          Hidden rather than disabled at one signer. A request with nobody on it
          cannot be sent, so the control would never be usable — and a greyed
          button that is permanently grey reads as a bug.
        */}
        {signerCount > 1 && (
          <div className='pt-2 border-t border-gray-100'>
            <button
              type='button'
              onClick={onRemove}
              className='flex items-center gap-1.5 text-sm font-medium text-red-600'
            >
              <Trash2 size={14} />
              Remove this signer
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}

function PanelField({ label, required = false, children }) {
  return (
    <div>
      <p className='text-xs font-medium text-gray-500 mb-1.5'>
        {label}
        {required && (
          /* aria-hidden with a sr-only word beside it: a bare "*" is announced
             as "star" or skipped entirely depending on the screen reader, which
             is not what it means. */
          <>
            <span aria-hidden className='ml-0.5 text-red-500'>
              *
            </span>
            <span className='sr-only'> (required)</span>
          </>
        )}
      </p>
      {children}
    </div>
  );
}
